import moment from 'moment'
import { dayDifference, today } from 'utils/time'
import { combineReducers } from 'redux'
import { loadingActions } from 'store/loader'
import { DEFAULT_NA_STRING_SORT_VAL, DATE_FORMAT_MAP } from 'utils/constants'
import request from 'utils/request'
import { getStudyVersionID } from '../../../../../../../../../../utils/studyConfigUtils'
import { hasPiiRole, getUserRoleByStudy } from 'utils/misc'

//
// Actions
//
export const FETCHED_CAREGIVERS = 'FETCHED_CAREGIVERS'
export const FETCHED_CAREGIVERS_ASSIGNMENT = 'FETCHED_CAREGIVERS_ASSIGNMENT'

export function _fetchCaregivers(studyID) {
  return (dispatch, getState) => {
    const studyVersionID = getStudyVersionID(getState()?.location)
    const success = jsonBody => {
      return Promise.resolve(jsonBody.caregivers)
    }

    return dispatch(
      request({
        url: `/control/studies/${studyID}/study_version/${studyVersionID}/caregivers`,
        success,
        hasLoader: true,
        loadingKey: 'caregivers',
      }),
    ).then(payload => {
      return Promise.resolve(payload)
    })
  }
}

export const fetchCaregivers = studyID => dispatch => {
  dispatch(loadingActions.startLoader(true))
  return dispatch(_fetchCaregivers(studyID)).then(payload => {
    dispatch(loadingActions.stopLoader(true))
    dispatch(parseCaregiversMap(payload))
  })
}

const parseCaregiversMap = rawCaregiversMap => {
  return (dispatch, getState) => {
    const { study, user, subroute, caregivers } = getState()
    const isPiiRole = hasPiiRole(getUserRoleByStudy(user, study.currentStudy.id))
    const resultCaregiversMap = {}
    const resultCaregiverAssignmentMap = {}

    const _startedCaregivers = []
    const _noActiveData = []
    const _noPassiveData = []
    const _accountDeletionRequestData = []
    const _accountSetupData = []
    const _accountRecoveryData = []

    const caregiverIdArr = Object.keys(rawCaregiversMap || {})

    const CAREGIVER_STATUS_MAP = {
      accountDeletionRequest: 'account-deletion-request',
      accountRecovery: 'account-recovery',
      completeAccountSetup: 'complete-account-setup',
      compliant: 'compliant',
      noActiveData: 'no-active-data',
      noPassiveData: 'no-passive-data',
      started: 'started',
    }

    caregiverIdArr.forEach(caregiverId => {
      const caregiver = rawCaregiversMap[caregiverId]

      const noPii = !caregiver.fname && !caregiver.lname
      const presentDateTime = moment()
      const firstName = noPii ? `${caregiver.fname || '---'}` : `${caregiver.fname}`
      const lastName = noPii ? `${caregiver.lname || '---'}` : `${caregiver.lname}`
      const firstLogin = moment(caregiver.first_login || caregiver.created_at)
      const lastInstrument = moment(caregiver.last_instrument_timestamp || 0)
      const lastPassiveData = moment(caregiver.last_passive_data_timestamp || 0)

      const daysSinceFirstLogin = dayDifference(firstLogin, presentDateTime)
      const daysSinceLastInstrument = dayDifference(lastInstrument, presentDateTime)
      const daysSinceLastPassiveData = dayDifference(lastPassiveData, presentDateTime)

      const registrationDate = moment(caregiver.created_at)

      let cohortsText = caregiver.study_groups
        ? Object.values(caregiver.study_groups)
            .sort()
            .join(', ')
        : ''
      if (cohortsText && cohortsText.length > 15) cohortsText = `${cohortsText.slice(0, 15)}...`
      else if (!cohortsText) cohortsText = 'None'

      const hasNoPassive = isFinite(daysSinceLastPassiveData) && daysSinceLastPassiveData > 1
      const hasNoActive = isFinite(daysSinceLastInstrument) && daysSinceLastInstrument > 14
      const hasAccountDeletionRequest = caregiver.account_deletion_status === 'pending'

      /**
       * This caregiver status is only valid for emailless studies where @completed_account_setup
       * is explicitly sent and not null or undefined. If the value is null or undefined, we don't
       * classify the caregiver as being @isInAccountSetup .
       */
      const isInAccountSetup = !caregiver.completed_account_setup && !(caregiver.completed_account_setup == null)

      const isInAccountRecovery = caregiver.account_recovery_support && !(caregiver.account_recovery_support == null)

      let _complianceString = CAREGIVER_STATUS_MAP.compliant
      const registration = {
        value: registrationDate.format(DATE_FORMAT_MAP.mainWithDateTime),
        sortValue: Math.floor(registrationDate.valueOf() / 1000),
        moment: registrationDate,
      }

      const hasLoggedIn = !!caregiver.first_login

      if (hasAccountDeletionRequest) {
        _accountDeletionRequestData.push(caregiver.id)
        _complianceString = CAREGIVER_STATUS_MAP.accountDeletionRequest
      } else if (isInAccountSetup) {
        _accountSetupData.push(caregiver.id)
        _complianceString = CAREGIVER_STATUS_MAP.completeAccountSetup
      } else if (hasNoPassive && hasNoActive) {
        _noActiveData.push(caregiver.id)
        _noPassiveData.push(caregiver.id)
        _complianceString = `${CAREGIVER_STATUS_MAP.noPassiveData} ${CAREGIVER_STATUS_MAP.noActiveData}`
      } else if (hasNoPassive) {
        _noPassiveData.push(caregiver.id)
        _complianceString += ` ${CAREGIVER_STATUS_MAP.noPassiveData}`
      } else if (hasNoActive) {
        _noActiveData.push(caregiver.id)
        _complianceString = CAREGIVER_STATUS_MAP.noActiveData
      }
      if (hasLoggedIn) {
        _startedCaregivers.push(caregiver.id)
        _complianceString += ` ${CAREGIVER_STATUS_MAP.started}`
        if (isInAccountRecovery) {
          _accountRecoveryData.push(caregiver.id)
          _complianceString += ` ${CAREGIVER_STATUS_MAP.accountRecovery}`
        }
      }

      const firstLoginText = !hasLoggedIn
        ? `Never Logged In Resend Invite (registered ${moment(caregiver.created_at).format(DATE_FORMAT_MAP.main)})`
        : `${firstLogin.format(DATE_FORMAT_MAP.main)} (${daysSinceFirstLogin} days)`

      const row = [
        {
          key: 'id',
          value: caregiver.id,
          sortValue: caregiver.id,
          className: 'id-row',
          isPtpDisabled: caregiver.opt_out || false,
        },
        {
          key: 'subjectId',
          value: caregiver.subject_id || '---',
          sortValue: caregiver.subject_id || DEFAULT_NA_STRING_SORT_VAL,
          className: 'subject-id-row',
        },
        isPiiRole
          ? { key: 'lastName', value: lastName || '---', sortValue: lastName || '---', className: 'name-row' }
          : null,
        isPiiRole
          ? { key: 'firstName', value: firstName || '---', sortValue: firstName || '---', className: 'name-row' }
          : null,
        { key: 'siteId', value: caregiver.site_id, sortValue: caregiver.site_id, className: 'site-id-row' },
        { key: 'siteName', value: caregiver.site_name, sortValue: caregiver.site_name, className: 'site-name-row' },
        {
          key: 'firstLogin',
          value: firstLoginText,
          sortValue: caregiver.first_login ? firstLogin.valueOf() : firstLogin.valueOf() / 1000,
          daysSinceFirstLogin,
          moment: firstLogin,
          username: caregiver.username,
          caregiverId: caregiver.id,
        },
      ]
      const rowWithCheckbox = [
        {
          key: 'checkbox',
          value: caregiver.id,
          sortValue: null,
          compliance: _complianceString,
          siteID: caregiver.site_id,
        },
        {
          key: 'relationToParticipant',
          value: null,
          sortValue: null,
          compliance: _complianceString,
          siteID: caregiver.site_id,
        },
        ...row,
      ]
      row.props = { compliance: _complianceString, registration }
      rowWithCheckbox.props = { compliance: _complianceString, registration }

      resultCaregiversMap[caregiverId] = row.filter(item => !!item)
      resultCaregiverAssignmentMap[caregiverId] = rowWithCheckbox.filter(item => !!item)
    })

    dispatch({
      type: FETCHED_CAREGIVERS,
      payload: resultCaregiversMap,
    })

    dispatch({
      type: FETCHED_CAREGIVERS_ASSIGNMENT,
      payload: resultCaregiverAssignmentMap,
    })
  }
}

const caregivers = (state = {}, action) => {
  switch (action.type) {
    case FETCHED_CAREGIVERS:
      return action.payload
    default:
      return state
  }
}

const caregiversAssignment = (state = {}, action) => {
  switch (action.type) {
    case FETCHED_CAREGIVERS_ASSIGNMENT:
      return action.payload
    default:
      return state
  }
}

export default combineReducers({
  caregivers,
  caregiversAssignment,
})

export const actions = {
  fetchCaregivers,
}
